import { useState } from "react";
import { Tooltip } from "antd";
import { DownloadOutlined, PictureOutlined } from "@ant-design/icons";
import type { Texture } from "@textureninja/shared";
import { CDN_URL, DOWNLOAD_URL } from "@/theme.js";
import { useRecordDownload } from "@/lib/queries.js";

interface Props {
  texture: Texture;
  width: number;
  height: number;
  onOpen: (t: Texture) => void;
}

export function downloadTexture(
  filename: string,
  id: number,
  recordDownload: (id: number) => void,
) {
  const link = document.createElement("a");
  link.href = DOWNLOAD_URL + filename;
  link.style.display = "none";
  link.setAttribute("download", filename);
  if (typeof link.download === "undefined") link.setAttribute("target", "_blank");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  recordDownload(id);
}

export function Thumbnail({ texture, width, height, onOpen }: Props) {
  const [hover, setHover] = useState(false);
  const recordDownload = useRecordDownload();
  const hasChildren = !!texture.children?.length;
  const childCount = (texture.children?.length ?? 0) + 1;

  function handleDownload(e: React.MouseEvent) {
    e.stopPropagation();
    downloadTexture(texture.key, texture.id, recordDownload.mutate);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(texture)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen(texture)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        width,
        height,
        cursor: "pointer",
        overflow: "hidden",
        borderRadius: 3,
        background: "#1c1d22",
        boxShadow: hover
          ? "inset 0 0 0 1px rgba(255,255,255,0.18), inset 0 0 0 2px rgba(0,0,0,0.5)"
          : "inset 0 0 0 1px rgba(0,0,0,0.5)",
        transition: "box-shadow 140ms ease",
      }}
    >
      <img
        src={`${CDN_URL}thumb-${texture.key}`}
        alt={texture.key}
        loading="lazy"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          filter: hover ? "brightness(1.05)" : "brightness(0.95)",
          transition: "filter 200ms ease",
        }}
      />

      {hasChildren && (
        <Tooltip title={`${childCount} images`}>
          <div
            style={{
              position: "absolute",
              top: 6,
              left: 6,
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              padding: "1px 6px",
              fontSize: 10,
              fontWeight: 600,
              color: "rgba(255,255,255,0.9)",
              background: "rgba(0,0,0,0.7)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 2,
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.02em",
            }}
          >
            <PictureOutlined style={{ fontSize: 10 }} />
            <span>{childCount}</span>
          </div>
        </Tooltip>
      )}

      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.0) 45%)",
          opacity: hover ? 1 : 0,
          transition: "opacity 140ms ease",
          pointerEvents: "none",
        }}
      />

      <div
        style={{
          position: "absolute",
          right: 6,
          bottom: 6,
          opacity: hover ? 1 : 0,
          transition: "opacity 140ms ease",
          pointerEvents: hover ? "auto" : "none",
        }}
      >
        <Tooltip title="Download">
          <button
            type="button"
            onClick={handleDownload}
            aria-label="Download texture"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "rgba(0,0,0,0.65)",
              color: "#fff",
              cursor: "pointer",
              transition: "background 140ms ease, border-color 140ms ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--ant-color-primary)";
              e.currentTarget.style.borderColor = "var(--ant-color-primary)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(0,0,0,0.65)";
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
            }}
          >
            <DownloadOutlined style={{ fontSize: 13 }} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}
